import { RoutePostCategories_categoryId_Stores_storeId_FixturesPog } from "@CommonApi/categories/_categoryId_/stores/_storeId_/fixturesPog";
import { Pog } from "@CommonTypes/merchflow/pog/pog";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useApi } from "src/api";
import { Page, Planogram } from "src/components";
import { useLoaders } from "src/components/InlineLoader";
import { MerchflowHeader } from "src/components/merchflows/MerchflowHeader/MerchflowHeader";
import { PlanogramToolbar } from "src/components/Planogram/components/Toolbar/PlanogramToolbar";
import { PlanogramHovererDetails } from "src/components/PlanogramExtensions/PlanogramHovererDetails/PlanogramHovererDetails";
import { PlanogramMetrics } from "src/components/PlanogramExtensions/PlanogramMetrics";
import { Button, Flex } from "src/elements";
import { useModifiedPog } from "src/hooks/useModifiedPogAtom";
import { pages } from "src/utils";
import { omniAtom } from "src/utils/atoms";
import { sleep } from "src/utils/sleep";

const pogAtom = omniAtom({
  prefix: pages.categories_categoryId_stores_storeId_Fixtures,
  key: "pog",
  value: null as null | Pog,
});

export const PageCategories_categoryId_Stores_storeId_Fixtures = () => {
  const { categoryId: _categoryId, storeId: _storeId } = useParams<{
    categoryId: string;
    storeId: string;
  }>();
  const categoryId = Number(_categoryId);
  const storeId = Number(_storeId);

  const { pog, setPog, revertPog, isModified, savePogAsOriginal } = useModifiedPog(pogAtom);
  const {
    getCategories_categoryId_Stores_storeId_FixturesPogApi,
    postCategories_categoryId_Stores_storeId_FixturesPogApi,
  } = useApi();
  const { hasLoader } = useLoaders();
  const [isSaveSuccessful, setIsSaveSuccessful] = useState(false);
  const isSaveLoading = hasLoader(RoutePostCategories_categoryId_Stores_storeId_FixturesPog);

  useEffect(() => {
    loadFixturesPog(categoryId, storeId);
  }, [categoryId, storeId]);

  useEffect(() => {
    return () => {
      setPog(null);
    };
  }, []);

  const loadFixturesPog = async (categoryId: number, storeId: number) => {
    const response = await getCategories_categoryId_Stores_storeId_FixturesPogApi({
      categoryId: String(categoryId),
      storeId: String(storeId),
    });
    if (response) {
      setPog(response);
    }
  };

  const onSave = async () => {
    if (!pog) return;

    const response = await postCategories_categoryId_Stores_storeId_FixturesPogApi(
      { categoryId: String(categoryId), storeId: String(storeId) },
      pog.toNonCircular(),
    );

    if (response !== null) {
      setIsSaveSuccessful(true);
      await sleep(1000);
      savePogAsOriginal();
      setIsSaveSuccessful(false);
    }
  };

  return (
    <Page>
      <MerchflowHeader categoryId={categoryId} storeId={storeId} />

      <PlanogramToolbar pogAtom={pogAtom} showProducts={false} />

      <Flex minHeight="0" flexGrow column>
        <Planogram
          pogAtom={pogAtom}
          isEditable
          isPanelVisible
          isPog
          isWithBorder
          showPanelProducts={false}
        />
      </Flex>

      <PlanogramHovererDetails />

      <Flex justify="between" align="center">
        <PlanogramMetrics pogAtom={pogAtom} />

        {isModified && (
          <Flex gap="10px">
            <Button
              color="red"
              variant="inverted"
              isDisabled={isSaveLoading || isSaveSuccessful}
              onClick={revertPog}
            >
              Revert Changes
            </Button>

            <Button isLoading={isSaveLoading} isSuccessful={isSaveSuccessful} onClick={onSave}>
              Save
            </Button>
          </Flex>
        )}
      </Flex>
    </Page>
  );
};
